import { Video } from 'api';
import { getPlayerZone } from 'components/pages/embed/zones';
import useVideoPlayerStore from 'components/video-player/store';
import type { TalkPageActionProps } from 'lib/pages/talks/slug/[...slug].props';
import { getProps } from 'lib/pages/talks/slug/getProps';
import type { GetServerSideProps } from 'next';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { useEffect, useMemo } from 'react';
import { parse } from 'url';

// Dynamically import components with loading optimization
const Hero = dynamic(() => import('components/pages/talks/slug/hero'), {
  ssr: true,
  loading: () => (
    <div className="aspect-video w-full animate-pulse bg-gray-900" />
  )
});

const TalkPageContextProvider = dynamic(
  () =>
    import('components/pages/talks/slug/talk-page-context').then(
      mod => mod.TalkPageContextProvider
    ),
  { ssr: true }
);

type EmbedProps = {
  videoData: Video;
};

// Memoize the player zone calculation
const usePlayerZone = (zone?: string, partner?: string) => {
  return useMemo(() => {
    if (partner) return getPlayerZone(partner);
    if (zone) return getPlayerZone(zone);
    return undefined;
  }, [zone, partner]);
};

export default function Embed({ videoData }: EmbedProps): React.ReactNode {
  const router = useRouter();
  const { zone, partner } = router.query;

  const { onEmbed, isEmbed } = useVideoPlayerStore(state => ({
    onEmbed: state.onEmbed,
    isEmbed: state.isEmbed
  }));

  const playerZone = usePlayerZone(zone as string, partner as string);

  useEffect(() => {
    onEmbed(true, playerZone);
    return () => {
      onEmbed(false, undefined);
    };
  }, [isEmbed, playerZone, onEmbed]);

  const pageTitle = useMemo(
    () => `${videoData?.title || ''} | TED Talk Embed`,
    [videoData?.title]
  );

  return (
    <TalkPageContextProvider>
      <Head>
        <title>{pageTitle}</title>
        <meta name="title" content={videoData?.title || ''} />
      </Head>

      <div className="bg-black">
        <div className="relative max-w-full">
          <Hero video={videoData} embed />
        </div>
      </div>
    </TalkPageContextProvider>
  );
}

export const getServerSideProps: GetServerSideProps = async context => {
  const { res, query, req } = context;

  res.setHeader('Cache-Control', 'no-transform, public, max-age=180');

  const parsedUrl = parse(req.url!, true);
  const path = parsedUrl.pathname || '';

  // Remove the '/embed/' prefix and .html suffix if present
  const slug = path.replace(/^\/embed\//, '').replace(/\.html$/, '');

  // Validate the slug format
  const isValidSlug = (s: string): boolean => {
    if (!s.trim()) return false;

    // Optimize regex patterns
    const validSlugPattern =
      /^(?:[a-z]{2,3}\/)?[a-z0-9][a-z0-9_]+(?:\/[a-z0-9][a-z0-9_]+)*(?:#t=\d+)?$/;
    if (!validSlugPattern.test(s)) return false;

    // Use Set for faster lookups
    const suspiciousPatterns = new Set([
      /\.[a-z]{2,4}$/i,
      /^(?:static|assets|images|js|css|api)/i,
      /^(?:blob|data|file):/i,
      /%[0-9A-F]{2}/i,
      /[{}[\]<>]/
    ]);

    return ![...suspiciousPatterns].some(pattern => pattern.test(s));
  };

  // Return 404 for invalid slugs
  if (!isValidSlug(slug)) {
    return { notFound: true };
  }

  // Handle .html redirect
  if (path.endsWith('.html')) {
    return {
      redirect: {
        destination: `/embed/${slug}${parsedUrl.search || ''}`,
        permanent: true
      }
    };
  }

  const updatedQuery = { ...query, slug: slug.split('/') };

  try {
    const props: TalkPageActionProps = await getProps({
      query: updatedQuery,
      asPath: path,
      isEmbed: true
    });

    if (props.action?.type === 'redirect') {
      return {
        redirect: {
          destination: props.action.location,
          permanent: props.action.code === 301
        }
      };
    }

    if (props.action?.code) {
      res.statusCode = props.action.code;
    }

    if (!props.videoData) {
      return { notFound: true };
    }

    // Only return necessary data
    return {
      props: {
        videoData: props.videoData,
        action: props.action || null
      }
    };
  } catch {
    return { notFound: true };
  }
};
